import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  FlagIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "lucide-react";

import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";
import BetaRequestsAdmin from "../components/admin/BetaRequestsAdmin";
import GolfCoursesAdmin from "../components/admin/GolfCoursesAdmin";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();

  const [activeSection, setActiveSection] = useState("beta");
  const [pendingBetaCount, setPendingBetaCount] = useState(0);
  const [pendingCourseCount, setPendingCourseCount] = useState(0);

  const loadPendingCounts = useCallback(async () => {
    if (!user) return;

    const [betaResult, courseResult] = await Promise.allSettled([
      supabase.functions.invoke("manage-beta-access", {
        body: {
          action: "list",
          status: "pending",
        },
      }),
      supabase.functions.invoke("manage-golf-courses", {
        body: {
          action: "list",
          status: "pending",
        },
      }),
    ]);

    if (betaResult.status === "fulfilled") {
      const { data, error } = betaResult.value;

      if (!error && data?.success) {
        setPendingBetaCount(
          Array.isArray(data.requests) ? data.requests.length : 0
        );
      }
    }

    if (courseResult.status === "fulfilled") {
      const { data, error } = courseResult.value;

      if (!error && data?.success) {
        setPendingCourseCount(
          Array.isArray(data.courses) ? data.courses.length : 0
        );
      }
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadPendingCounts();
    }
  }, [authLoading, user, loadPendingCounts]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 text-sm text-[#183d2d] shadow-sm">
          Kontrollerar din session…
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.1)]">
          <header className="relative overflow-hidden bg-[#183d2d] px-5 py-6 text-white sm:px-8 sm:py-8">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />

            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-white/55 transition hover:text-white"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Till startsidan
                </Link>

                <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#dcc78f]">
                  HoleInOne
                </p>

                <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                  Administration
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/60">
                  Hantera åtkomstansökningar, golfbanor och andra administrativa
                  delar av HoleInOne.
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-3">
                <ShieldCheckIcon className="h-5 w-5 text-[#dcc78f]" />

                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/45">
                    Administratör
                  </p>

                  <p className="mt-0.5 text-xs font-semibold text-white">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
          </header>

          <nav className="border-b border-[#e4ded1] bg-[#f7f4ec] px-4 py-3 sm:px-6">
            <div className="flex flex-wrap gap-2">
              <SectionTab
                active={activeSection === "beta"}
                onClick={() => setActiveSection("beta")}
                icon={UsersIcon}
                label="Betaansökningar"
                count={pendingBetaCount}
              />

              <SectionTab
                active={activeSection === "courses"}
                onClick={() => setActiveSection("courses")}
                icon={FlagIcon}
                label="Golfbanor"
                count={pendingCourseCount}
              />
            </div>
          </nav>

          {activeSection === "beta" ? (
            <BetaRequestsAdmin onChanged={loadPendingCounts} />
          ) : (
            <GolfCoursesAdmin onChanged={loadPendingCounts} />
          )}
        </div>
      </main>
    </div>
  );
}

function SectionTab({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-xs font-semibold transition ${
        active
          ? "border-[#183d2d] bg-[#183d2d] text-white"
          : "border-[#d3ccbf] bg-white text-[#68716c] hover:border-[#183d2d] hover:text-[#183d2d]"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}

      {count > 0 && (
        <span
          className={`min-w-5 rounded-full px-1.5 py-0.5 text-center text-[9px] font-bold ${
            active
              ? "bg-[#b08b45] text-white"
              : "bg-[#f2ead9] text-[#8a6623]"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}